import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Eye, Edit, Trash2, Loader, User, Mail, Phone, TrendingUp, CheckCircle, Calendar, ArrowRightLeft, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { useApiData, useMutation } from '../../hooks/useApi';
import Modal from '../common/Modal';
import TransferModal from '../common/TransferModal';

const ReferralPartners = ({ user, selectedCountry = 'all', onNavigateToAdmissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [viewingPartner, setViewingPartner] = useState(null);
  const [deletingPartner, setDeletingPartner] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferringPartner, setTransferringPartner] = useState(null);

  const { data: partnersData, loading, refetch } = useApiData(
    () => {
      const params = { search: searchTerm };
      if (selectedCountry && selectedCountry !== 'all') {
        params.country_id = selectedCountry;
      }
      return apiService.getReferralPartners(params);
    },
    [searchTerm, selectedCountry]
  );

  const { mutate: createPartner, loading: creating } = useMutation(apiService.createReferralPartner.bind(apiService));
  const { mutate: updatePartner, loading: updating } = useMutation(apiService.updateReferralPartner.bind(apiService));
  const { mutate: deletePartner, loading: deleting } = useMutation(apiService.deleteReferralPartner.bind(apiService));

  const partners = Array.isArray(partnersData?.data) ? partnersData.data :
    Array.isArray(partnersData) ? partnersData : [];

  // Listen for dashboard quick action events
  useEffect(() => {
    const handleOpenModal = () => {
      setShowAddModal(true);
      setEditingPartner(null);
      setFormData({ name: '', email: '', phone: '' });
    };

    window.addEventListener('open-referral-partner-modal', handleOpenModal);
    return () => window.removeEventListener('open-referral-partner-modal', handleOpenModal);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await updatePartner(editingPartner.id, formData);
        toast.success('Partner updated successfully');
        setEditingPartner(null);
        setShowAddModal(false);
      } else {
        const submitData = { ...formData };
        if (selectedCountry && selectedCountry !== 'all') {
          submitData.country_id = selectedCountry;
        }
        await createPartner(submitData);
        toast.success('Partner created successfully');
        setShowAddModal(false);
      }
      setFormData({ name: '', email: '', phone: '' });
      refetch();
    } catch (error) {
      toast.error(error.message || 'Operation failed');
    }
  };

  const handleDelete = (partner) => {
    setDeletingPartner(partner);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deletePartner(deletingPartner.id);
      toast.success('Partner deleted successfully');
      refetch();
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setShowDeleteModal(false);
      setDeletingPartner(null);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email,
      phone: partner.phone || ''
    });
    setShowAddModal(true);
  };

  const handleViewDetails = (partner) => {
    setViewingPartner(partner);
    setShowDetailsModal(true);
  };

  const handleTransfer = (partner) => {
    setTransferringPartner(partner);
    setShowTransferModal(true);
  };

  const handleTransferComplete = () => {
    refetch();
    setShowTransferModal(false);
    setTransferringPartner(null);
  };

  const handleViewApplications = (partner) => {
    if (onNavigateToAdmissions) {
      onNavigateToAdmissions(partner.id, partner.name, { filterByPartner: true });
    }
  };

  if (loading) {
    return (
      <div className="responsive-card">
        <div className="responsive-loading">
          <div className="responsive-spinner" />
          <p>Loading Business Partners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-section">
      <div className="section-header">
        <h2>Business Partners</h2>
        {(user?.role === 'super_admin' || user?.role === 'team_lead') && (
          <motion.button
            className="btn-primary touch-target"
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setShowAddModal(true);
              setEditingPartner(null);
              setFormData({ name: '', email: '', phone: '' });
            }}
          >
            <Plus size={16} />
            <span className="responsive-text">Add New Partner</span>
          </motion.button>
        )}
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search partners..."
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />

      </div>

      <div className="table-container table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Academic Counsellor Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Referrals</th>
              <th>Completed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((partner, index) => (
              <motion.tr
                key={partner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
              >
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="user-avatar" style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}>
                      {partner.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span>{partner.name}</span>
                  </div>
                </td>
                <td>{partner.teamLeadName ? partner.teamLeadName : '-'}</td>
                <td>{partner.email}</td>
                <td>{partner.phone || 'N/A'}</td>
                <td>
                  <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{partner.referrals || 0}</span>
                </td>
                <td>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>{partner.completedReferrals || 0}</span>
                </td>

                <td>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <motion.button
                      className="btn-sm touch-target"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleViewDetails(partner)}
                      title="View Details"
                    >
                      <Eye size={12} />
                    </motion.button>
                    {(user?.role === 'super_admin' || user?.role === 'admin') && (
                      <>
                        {/* <motion.button
                          className="btn-sm info touch-target"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleTransfer(partner)}
                          title="Transfer Partner"
                        >
                          <ArrowRightLeft size={12} />
                        </motion.button> */}
                        <motion.button
                          className="btn-sm info touch-target"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleViewApplications(partner)}
                          title="View Applications"
                        >
                          <FileText size={12} />
                        </motion.button>
                        <motion.button
                          className="btn-sm warning touch-target"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleEdit(partner)}
                          title="Edit Partner"
                        >
                          <Edit size={12} />
                        </motion.button>
                        <motion.button
                          className="btn-sm danger touch-target"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDelete(partner)}
                          disabled={deleting}
                          title="Delete Partner"
                        >
                          <Trash2 size={12} />
                        </motion.button>
                      </>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingPartner(null);
        }}
        title="Confirm Delete"
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <p>Are you sure you want to delete this partner?</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-sm danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              Delete
            </button>
            <button
              className="btn-sm"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingPartner(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Refferal Partner Details"
      >
        {viewingPartner && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                {viewingPartner.name.split(' ').map(n => n[0]).join('')}
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{viewingPartner.name}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>Referral Partner</p>
            </div>

            {/* Contact Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Contact Information</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <Mail size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151' }}>{viewingPartner.email}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <Phone size={18} style={{ color: '#6b7280' }} />
                  <span style={{ color: '#374151' }}>{viewingPartner.phone || 'Not provided'}</span>
                </div>
              </div>
            </div>

            {/* Stats Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Performance Stats</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <TrendingUp size={24} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                    {viewingPartner.referrals || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', opacity: '0.9' }}>Total Referrals</div>
                </div>
                <div style={{
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '12px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <CheckCircle size={24} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0.5rem 0' }}>
                    {viewingPartner.completedReferrals || 0}
                  </div>
                  <div style={{ fontSize: '0.875rem', opacity: '0.9' }}>Completed</div>
                </div>
              </div>
            </div>

            {/* Success Rate */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ color: '#374151', fontWeight: '500' }}>Success Rate</span>
                <span style={{ color: '#6b7280' }}>
                  {viewingPartner.referrals > 0
                    ? Math.round((viewingPartner.completedReferrals / viewingPartner.referrals) * 100)
                    : 0}%
                </span>
              </div>
              <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  width: `${viewingPartner.referrals > 0 ? (viewingPartner.completedReferrals / viewingPartner.referrals) * 100 : 0}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #10b981, #059669)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: '#dcfce7',
                color: '#166534',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{ width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%' }} />
                {viewingPartner.status || 'Active'}
              </span>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={editingPartner ? 'Edit Partner' : 'Add New Partner'}
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={creating || updating}
            >
              {creating || updating ? (
                <>
                  <Loader className="spin" size={16} />
                  Saving...
                </>
              ) : (
                editingPartner ? 'Update' : 'Create'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Transfer Modal */}
      <TransferModal
        isOpen={showTransferModal}
        onClose={() => {
          setShowTransferModal(false);
          setTransferringPartner(null);
        }}
        transferType="referral_partner"
        fromUser={transferringPartner}
        onTransferComplete={handleTransferComplete}
      />
    </div>
  );
};

export default ReferralPartners;